import { readProfilePolicy, applyProfilePolicy, profileFieldDefaults } from 'src/app/shared/profile/profile-field-policy';
import { AddressAutocompleteComponent } from 'src/app/shared/address-check/address-autocomplete.component';
import { PhoneVerifyComponent } from 'src/app/shared/phone-verify/phone-verify.component';
import { fileUploadService } from 'src/app/shared/services/fileUpload.service';
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CountriesService } from 'src/app/shared/services/countries.service';
import { GlobalService } from 'src/app/shared/services/global.service';
import { TenantService } from 'src/app/tenant/tenant.service';
import { AuthService } from '../auth.service';
import { Signup } from './signup.model';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { MemberService } from 'src/app/shared/services/member.service';
import { AppService } from 'src/app/shared/services/app.service';
import { Location } from '@angular/common';
import { OccupationService } from 'src/app/shared/profile/profile/occupation.service';
import { Extension, Occupation } from 'src/app/shared/profile/profile/profile.model';
import { MemberConfiguration } from 'src/app/shared/profile/profile/member-configuration.model';
import { ProfileService } from 'src/app/shared/profile/profile/profile.service';
import { SelfCheckinFlowService } from '../../self-checkin/self-checkin-flow.service';

@Component({
    selector: 'app-signup',
    templateUrl: './signup.component.html',
    styleUrls: ['./signup.component.scss'],
    standalone: false
})
export class SignupComponent implements OnInit, AfterViewInit, OnDestroy {
  fieldPolicy = {...profileFieldDefaults};
  fieldHidden(key: string) { return this.fieldPolicy?.[key] === 'hidden'; }
  fieldRequired(key: string) { return (this.fieldPolicy?.[key] || profileFieldDefaults[key]) === 'required'; }

  @ViewChild(PhoneVerifyComponent) phoneVerifier: PhoneVerifyComponent;
  @ViewChild(AddressAutocompleteComponent) addressAutocomplete: AddressAutocompleteComponent;
  @ViewChild('stepHeading') stepHeading: ElementRef<HTMLElement>;
  @ViewChild('stepHeader') stepHeader: ElementRef<HTMLElement>;
  signupStep = 0;
  churchLogo = '';
  phoneCountry = '';
  stepError = '';
  readonly stepTitles = ['', 'Your name and phone', 'Account access', 'About you', 'Address and church details', 'Review and create'];
  private phoneVerifiedAt = 0;
  private stepFocusTimer: number;
  private readonly stepFields = {
    1: ['firstName', 'lastName'],
    2: ['email', 'password', 'confirmPassword'],
    3: ['dateOfBirth', 'gender', 'maritalStatus', 'isMember', 'occupation', 'language'],
    4: ['street', 'city', 'state', 'zip', 'country', 'extensions']
  };

  goToStep(step: number) {
    this.signupStep = step;
    this.submit = false;
    this.stepError = '';
    clearTimeout(this.stepFocusTimer);
    this.stepFocusTimer = window.setTimeout(() => {
      const heading = this.stepHeading?.nativeElement;
      heading?.focus({ preventScroll: true });
      this.stepHeader?.nativeElement.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    });
  }

  phoneAccountExists = false;
  onPhoneAccountChecked(exists: boolean) {
    this.phoneAccountExists = exists;
    this.error = exists ? 'ACCOUNT_EXISTS' : null;
    if (exists) this.showSignupError();
  }

  validateStep(step: number): boolean {
    if (step === 1 && this.phoneAccountExists) return false;
    if (step === 3 && this.fieldRequired('picture') && !this.photoPreview) { this.stepError = 'Please add your profile picture.'; return false; }
    const fields: string[] = this.stepFields[step] || [];
    fields.forEach(name => this.signupForm.get(name)?.markAllAsTouched());
    if (fields.some(name => this.signupForm.get(name)?.invalid) ||
        (step === 2 && this.signupForm.errors?.passwordMismatch)) {
      this.stepError = 'Please check the highlighted fields before continuing.';
      return false;
    }
    if (step === 1 && (!this.isConfirmed || !this.phoneVerificationToken)) {
      this.stepError = 'Please verify your phone number before continuing.';
      return false;
    }
    return true;
  }

  get hasAddressStep() { return ['street','city','state','zip','country'].some(key => !this.fieldHidden(key)) || this.extensions.length > 0; }

  async nextStep() {
    if (this.signupStep > 0 && !this.validateStep(this.signupStep)) return;
    if (this.signupStep < 5) this.goToStep(this.signupStep === 3 && !this.hasAddressStep ? 5 : this.signupStep + 1);
  }

  previousStep() {  if (this.signupStep > 0) this.goToStep(this.signupStep === 5 && !this.hasAddressStep ? 3 : this.signupStep - 1); }

  changePhoneCountry(code: string) {
    if (code === this.phoneCountry) return;
    const country = this.countriesArr.find(country => country.code === code);
    if (!country) return;
    this.phoneCountry = code;
    this.dial_code = country.dial_code.replace(/\s/g, '');
    this.phoneVerifier?.Clear();
    this.UpdatePhone();
    this.stepError = 'Please verify your phone number with the selected dialing code.';
  }

  get phoneCountries() { return this.countriesArr?.filter(country => /^\+?[\d\s]+$/.test(country.dial_code)) || []; }

  get genderLabel() { const value=this.signupForm.get('gender').value; return value == null ? 'Not provided' : this.globalService.Get(value ? 'FEMALE' : 'MALE'); }
  get maritalStatusLabel() {
    if (this.signupForm.get('maritalStatus').value == null) return 'Not provided';
    return this.globalService.Get(['SINGLE', 'ENGAGED', 'MARRIED', 'WIDOWED', 'DIVORCED'][this.signupForm.get('maritalStatus').value]);
  }
  get occupationLabel() { return this.occupations.find(item => item.id === this.signupForm.get('occupation').value)?.name || 'Not provided'; }
  get countryLabel() { return this.countriesArr?.find(item => item.code === this.signupForm.get('country').value)?.name || ''; }

  signupPhoto: File = null;
  photoPreview = '';
  languages: string[] = ['English', 'Arabic'];
  photoError = '';
  photoUploading = false;
  private signupMemberId = 0;
  ngOnDestroy() { clearTimeout(this.stepFocusTimer); if (this.photoPreview) URL.revokeObjectURL(this.photoPreview); }
  selectPhoto(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { this.photoError = 'Please choose an image file.'; return; }
    this.removePhoto();
    this.signupPhoto = file;
    this.photoPreview = URL.createObjectURL(file);
  }
  removePhoto() {
    if (this.photoPreview) URL.revokeObjectURL(this.photoPreview);
    this.signupPhoto = null;
    this.photoPreview = '';
    this.photoError = '';
  }
  async uploadSignupPhoto() {
    if (!this.signupPhoto || this.photoUploading) return;
    if (!this.signupSessionReady || !(this.signupMemberId > 0)) {
      this.photoError = 'Your account is ready. Sign in and add your photo in My Profile.'; return;
    }
    this.photoUploading = true;
    this.photoError = '';
    try {
      const url = await this.fileUpload.uploadFile(this.signupPhoto, String(this.signupMemberId), 'profilepics');
      if (!url) throw new Error('No photo returned');
      this.signupPhoto = null;
    } catch {
      this.photoError = 'Your account is ready, but your photo could not be uploaded. Retry or add it later in My Profile.';
    } finally { this.photoUploading = false; }
  }
  churchName: string = '';
  isLoading: boolean = true;
  error: string = null;
  signupComplete: boolean = false;
  signupSessionReady = false;
  activationEmailSent = false;
  phoneVerificationToken = "";
  signupForm: FormGroup = new FormGroup({});
  submit: boolean = false;
  failureClosed: boolean = true;
  defaultCountry: string = '';
  minDate: Date = new Date(1900, 1, 1);
  maxDate: Date = new Date();
  dial_code: string = '';
  tenantSubdomain: string = '';
  showMemberStatusTip: boolean = false;
  hostPrefix: string = '';
  agreementAccepted: boolean = false;
  agreementUrl: string = "";
  textAccepted: boolean = true;
  emailAccepted = true;
  subscriptionError: boolean = false;
  phoneNumber: string = '';
  isConfirmed: boolean = false;
  activationRequired: boolean = false;
  occupations: Occupation[] = [];
  signupExtensions: Extension[] = [];
  memberConfigurations: MemberConfiguration[] = [];
  isConfigurationLoading: boolean = true;
  isSignupLoading: boolean = true;

  countriesArr: { name: string; dial_code: string; code: string }[];

  constructor(
    private authService: AuthService,
    private tenantService: TenantService,
    private countries: CountriesService,
    private globalService: GlobalService,
    private _router: Router,
    private appService: AppService,
    public _location: Location,
    private occupationService: OccupationService,
    private profileService: ProfileService,
    private fileUpload: fileUploadService,
    private fb: FormBuilder,
    private checkinFlow: SelfCheckinFlowService
  ) {}

  startupError = false;
  async ngOnInit(): Promise<void> {
    this.isLoading = true;
    this.startupError = false;
    try {
    const [Subscription, tenant, occupations, configurations, languages] = await Promise.all([
      this.appService.getSubscription(),
      this.tenantService.getSimpleTenant(),
      this.occupationService.getOccupationList(),
      this.profileService.getMemberConfiguration(),
      this.globalService.getLanguages ? this.globalService.getLanguages() : Promise.resolve(this.languages)
    ]);
    if (Subscription.memberActual >= Subscription.memberPlan)
    {
      this.isLoading = false;
      this.subscriptionError = true;
      return;
    }
    this.occupations = occupations;
    this.countriesArr = this.countries.CountriesList;

    this.fieldPolicy = readProfilePolicy(tenant.profileFieldPolicy);
    this.agreementUrl = tenant.agreementUrl;
    this.defaultCountry = tenant.defaultCountry;
    this.tenantSubdomain = this.tenantService.getTenant();
    this.phoneCountry = this.defaultCountry;
    this.tenantService.getTenantLogo(this.tenantSubdomain).then(logo => this.churchLogo = logo || '').catch(() => this.churchLogo = '');
    if (tenant.deleted == true) {
      this._router.navigate(['/service-notfound']);
      return;
    }
    this.churchName = tenant.name;
    this.languages = languages;

    this.dial_code = this.countries.CountriesList.filter(
      (a) => a.code == tenant.defaultCountry
    )[0].dial_code.replace(/\s/g, '');
    this.signupForm = new FormGroup(
      {
        firstName: new FormControl(null, [
          Validators.required,
          Validators.minLength(2),
        ]),
        lastName: new FormControl(null, [
          Validators.required,
          Validators.minLength(2),
        ]),
        email: new FormControl(null, [Validators.email]),
        language: new FormControl(tenant.language || 'English'),
        password: new FormControl(
          null,
          Validators.compose([
            Validators.required,
            Validators.pattern(
              '^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\\d]){1,})(?!.*\\s).{6,}$'
            ),
          ])
        ),
        //'^(?=.*?[A-Z])(?=(.*[a-z]){1,})(?=(.*[\\d]){1,})(?=(.*[\\W]){1,})(?!.*\\s).{7,}$'
        confirmPassword: new FormControl(null, [Validators.required]),
        dateOfBirth: new FormControl(null, [Validators.required]),
        street: new FormControl(null, [Validators.required]),
        city: new FormControl(null, [Validators.required]),
        state: new FormControl(null, [Validators.required]),
        zip: new FormControl(null, [Validators.required]),
        country: new FormControl(this.defaultCountry, [Validators.required]),
        gender: new FormControl(null, [Validators.required]),
        maritalStatus: new FormControl(null),
        occupation: new FormControl(null),
        phoneNumber: new FormControl(''),
        isMember: new FormControl(null, [Validators.required]),
        extensions: this.fb.array([]),
      },
      { validators: this.authService.passwordMismatchValidator }
    );

    applyProfilePolicy(this.signupForm, this.fieldPolicy);
    const checkinPhone = this.checkinFlow.verified(this.tenantSubdomain);
    if (this.checkinReturnUrl) this.signupStep = 1;
    if (checkinPhone) {
      this.dial_code = checkinPhone.dialCode;
      this.phoneCountry = checkinPhone.countryCode || this.countriesArr.find(country => country.dial_code.replace(/\s/g,'') === this.dial_code)?.code || this.phoneCountry;
      this.phoneNumber = checkinPhone.phoneNumber;
      this.phoneVerificationToken = checkinPhone.verificationToken;
      this.phoneVerifiedAt = checkinPhone.verifiedAt;
      this.isConfirmed = true;
      this.signupForm.patchValue({phoneNumber:this.phoneNumber});
    }
    this.memberConfigurations = configurations;
    this.memberConfigurations = this.generateOptions();

    this.isSignupLoading = false;
    this.isLoading =
      this.isSignupLoading ||  this.isConfigurationLoading;
    } catch {
      this.startupError = true;
    } finally {
      this.isLoading = false;
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
    this.globalService.selectedOption = "";
    this.globalService.title.push(this.globalService.screenHeader);
    this.globalService.screenHeader = this.globalService.isRTL ? 'إنشاء حساب' : 'Create Account';
    });
  }

  get extensions(): FormArray {
    return this.signupForm.get('extensions') as FormArray;
  }

  generateOptions() {
    let formExtensions = <FormArray>this.signupForm.controls.extensions;
    if (this.memberConfigurations != null && this.memberConfigurations.length > 0)
      this.memberConfigurations.forEach(a => {
        let selectable: boolean = a.options.indexOf(";") >= 0;
        var extension = this.fb.group({
          id: a.id,
          label: [a.label],
          options: selectable == true ? [a.options.split(';').map(item => item.trim())] : null,
          value: ['', a.mandatory == true ? Validators.required : Validators.nullValidator]
        });
        formExtensions.push(extension);
        if (a.options.indexOf(";") >= 0)
          a['optionsList'] = a.options.split(';').map(item => item.trim());
      });

    this.isConfigurationLoading = false;
    this.isLoading =
      this.isSignupLoading ||  this.isConfigurationLoading;
    return this.memberConfigurations;
  }


  getDomain() {
    if (environment.production == false)
      return "church-services-staging";
    return "church-services";
  }

  MemberStatusHelp() {
    this.showMemberStatusTip = !this.showMemberStatusTip;
  }

  OnCountryChange() {
    // Address country and verified phone dialing code are independent.
  }

  getExtensions() {
    let result: Extension[] = [];
    this.extensions.value.forEach(a => {
      let x: Extension = { key: a.id, value: a.value };
      result.push(x);
    });
    return result;
  }

  async onSubmit() {
    if (this.isLoading || this.signupComplete) return;
    if (this.signupStep < 5) { this.nextStep(); return; }
    for (let step = 1; step <= 4; step++) {
      if (!this.validateStep(step)) {
        const message = this.stepError;
        this.goToStep(step);
        this.stepError = message;
        return;
      }
    }
    if (!this.agreementAccepted) return;
    // The server proof lasts 30 minutes. Reverify early, retaining the form.
    if (!this.phoneVerifiedAt || Date.now() - this.phoneVerifiedAt >= 29 * 60 * 1000) {
      this.UpdatePhone();
      this.goToStep(1);
      this.stepError = 'Please verify your phone again to finish creating your account. Your other information is still here.';
      return;
    }
    this.submit = true;
    if (!this.signupForm.valid) {
      //      this.signupForm.get('email');
      return;
    }
    if (this.isConfirmed == false)
    {
      this.error = "VERIFY_PHONE_ERROR";
      this.isLoading = false;
      this.failureClosed = false;
      this.submit = false;
      return;
    }

    this.activationRequired = this.signupForm.get('email').value != null &&
      (this.signupForm.get('email').value || '').trim() != '';
    const signupData: Signup = {
      firstName: this.signupForm.get('firstName').value.trim(),
      lastName: this.signupForm.get('lastName').value.trim(),
      email: this.signupForm.get('email').value,
      disableText: !this.textAccepted,
      disableEmail: !this.emailAccepted,
      language: this.signupForm.get('language').value,
      password: this.signupForm.get('password').value.trim(),
      dateOfBirth: this.signupForm.get('dateOfBirth').value,
      street: (this.signupForm.get('street').value || '').trim(),
      addressVerificationToken: '',
      city: (this.signupForm.get('city').value || '').trim(),
      state: (this.signupForm.get('state').value || '').toUpperCase().trim(),
      zip: (this.signupForm.get('zip').value || '').trim(),
      country: this.signupForm.get('country').value,
      gender: this.signupForm.get('gender').value,
      maritalStatus: this.signupForm.get('maritalStatus').value,
      occupation: this.signupForm.get('occupation').value,
      phoneNumber: this.phoneNumber,
      phoneVerificationToken: this.phoneVerificationToken,
      dial_code: this.dial_code,
      extensionValues: this.getExtensions(),
      isMember: this.signupForm.get('isMember').value,
    };

    this.isLoading = true;
    var tempAgreement = this.agreementAccepted;
    this.authService.signup(signupData).then(
      async (data) => {
        if (data?.statusCode == 200)
        {
        // Establish the normal session; preserve the existing church agreement flow.
        const session = await this.authService.login(signupData.phoneNumber, signupData.password, false, 0, false).catch(() => null);
        this.signupSessionReady = session?.statusCode === 200;
        this.signupMemberId = session?.memberId || 0;
        await this.uploadSignupPhoto();
        this.signupForm.patchValue({ password: '', confirmPassword: '' });
        this.activationEmailSent = data.activationEmailSent === true;
        this.signupComplete = true;
        this.error = null;
        this.isLoading = false;
        this.submit = false;
        this.agreementAccepted = tempAgreement;
        if (this.checkinReturnUrl && this.signupSessionReady) this.returnToCheckin();
        }
        else
        {
          this.signupComplete = false;
          this.error = data?.statusMessage || "CONNECTION_ERROR";
          if (this.error === 'VERIFY_PHONE_ERROR') {
            this.UpdatePhone();
            this.goToStep(1);
            this.stepError = 'Please verify your phone again. Your other information is still here.';
          } else {
            const errorStep = {
              INVALID_FIRSTNAME: 1, INVALID_LASTNAME: 1, INVALID_DIALCODE: 1, INVALID_PHONENUMBER: 1,
              INVALID_EMAIL: 2, INVALID_DOB: 3, INVALID_STATE: 4, INVALID_ZIP: 4
            }[this.error];
            if (errorStep) this.goToStep(errorStep);
          }
          this.isLoading = false;
          this.failureClosed = false;
          this.showSignupError();
          this.submit = false;
          this.agreementAccepted = tempAgreement;
        }
      }).catch(
      (errorMessage) => {
        this.signupComplete = false;
        this.error = "CONNECTION_ERROR";
        this.isLoading = false;
        this.failureClosed = false;
        this.showSignupError();
        this.submit = false;
        this.agreementAccepted = tempAgreement;
      }
    );
  }
  private showSignupError() {
    setTimeout(() => {
      const alert = document.querySelector<HTMLElement>('.signup-submit-error');
      alert?.focus({ preventScroll: true });
      alert?.scrollIntoView({ block: 'center', behavior: 'auto' });
    });
  }

  continueAfterSignup(household: boolean) {
    if (this.checkinReturnUrl && !household) { this.returnToCheckin(); return; }
    const churchPath = '/' + this.tenantSubdomain;
    const destination = churchPath + (household ? '/myprofile' : '/home');
    if (this.signupSessionReady) {
      this._router.navigate([destination], household ? { queryParams: { setup: 'household' } } : {});
    } else {
      this._router.navigate([churchPath + '/login'], { queryParams: { returnUrl: destination + (household ? '?setup=household' : '') } });
    }
  }

  get checkinReturnUrl(): string { return this.checkinFlow.read(this.tenantSubdomain)?.returnUrl || ''; }
  returnToCheckin(): void {
    const destination = this.checkinReturnUrl;
    if (!destination) return;
    if (this.signupSessionReady) {
      this.checkinFlow.clear();
      void this._router.navigateByUrl(destination);
    } else {
      void this._router.navigate(['/' + this.tenantSubdomain + '/login'], {queryParams:{returnUrl:destination}});
    }
  }

  keyPressNumbers(event) {
    var charCode = event.which ? event.which : event.keyCode;
    // Only Numbers 0-9
    if (charCode < 32 || charCode > 126) {
      event.preventDefault();
      return false;
    } else {
      return true;
    }
  }

  ConfirmCellPhone(phoneNumber)
  {
    this.stepError = '';
    if (this.error === 'VERIFY_PHONE_ERROR') this.error = null;
    this.phoneVerifiedAt = Date.now();
    this.phoneNumber = phoneNumber;
    this.signupForm.patchValue({
      phoneNumber: phoneNumber});
    this.isConfirmed = true;
  }

  UpdatePhone()
  {
    this.phoneAccountExists = false;
    if (this.error === 'ACCOUNT_EXISTS') this.error = null;
    this.phoneVerifiedAt = 0;
    this.phoneNumber = "";
    this.signupForm.patchValue({
      phoneNumber: ''});
    this.isConfirmed = false;
    this.phoneVerificationToken = "";
  }

  failedVerification(){
    this.phoneVerificationToken = "";
    this.isConfirmed = false;
  }

  clearCellPhone()
  {
    this.phoneVerificationToken = "";
    this.phoneNumber = "";
    this.isConfirmed = false;
  }

  AcceptAgreement(check: any) {
    this.agreementAccepted = check;
  }

  AcceptText(check: any) {
    this.textAccepted = check;
  }

  gotoLogin()
  {
    this._router.navigate(['/' + this.tenantSubdomain + '/login'], this.checkinReturnUrl ? {queryParams:{returnUrl:this.checkinReturnUrl}} : {});
    this.subscriptionError = false;
  }
}
